import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  coerceLocale,
  fetchCatalogExercises,
  toCatalogItems,
  type Exercise,
  type ListCache,
} from "@jackedlogs/shared";
import type { CatalogItem, ProgramApiFetch } from "../ProgramEditor/types";

// ONE catalog hook for every picker surface (anti-monster S-C): loads
// the complete exercise catalog through the shared loader and resolves
// names to the active locale. Cache-first when the host injects a
// ListCache adapter (mobile's AsyncStorage mirror — offline gyms);
// web-rnw passes none and goes straight to the network. Locale switches
// re-resolve without a refetch.

export type UseCatalogItemsOptions = {
  apiFetch: ProgramApiFetch;
  cache?: ListCache<Exercise>;
};

export function useCatalogItems(opts: UseCatalogItemsOptions): {
  items: CatalogItem[];
  /** The unresolved catalog rows — for surfaces that need more than
   *  {slug, name} (e.g. ProgramPreview reads primary muscles). */
  raw: ReadonlyArray<Exercise> | null;
  ready: boolean;
} {
  const { i18n } = useTranslation();
  const locale = coerceLocale(i18n.language);
  const [raw, setRaw] = useState<ReadonlyArray<Exercise> | null>(null);

  // Refs so a host passing fresh literals per render can't re-trigger
  // the mount effect (the unstable-object-dep lesson).
  const apiRef = useRef(opts.apiFetch);
  const cacheRef = useRef(opts.cache);
  useEffect(() => {
    apiRef.current = opts.apiFetch;
    cacheRef.current = opts.cache;
  });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const cached = await cacheRef.current?.read();
      if (!cancelled && cached != null) setRaw(cached);
      try {
        const fresh = await fetchCatalogExercises(apiRef.current);
        if (cancelled) return;
        setRaw(fresh);
        void cacheRef.current?.write(fresh);
      } catch {
        // Offline / fetch failure: the cached snapshot (or empty list)
        // stands; the next mount retries.
        if (!cancelled) setRaw((prev) => prev ?? []);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const items = useMemo(
    () => (raw ? toCatalogItems(raw, locale) : []),
    [raw, locale],
  );

  return { items, raw, ready: raw !== null };
}
